import { useMutation } from '@apollo/client';
import { Button } from 'components/Button/Button';
import { Input } from 'components/Input/Input';
import dayjs from 'dayjs';
import { extractCaughtError, extractGraphQLError } from 'lib/graphql-error-handler';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BLOCKOUT_CREATE, BLOCKOUT_DELETE, ENTITIES } from 'services/reservations';

type Blockout = {
    readonly id: string;
    readonly startDatetime: string;
    readonly endDatetime: string;
    readonly reason: string | null;
};

type EntityBlockoutsDialogProps = {
    entityId: string;
    entityName: string;
    blockouts: readonly Blockout[];
    open: boolean;
    onClose: () => void;
};

const INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';
const DISPLAY_FORMAT = 'DD.MM.YYYY HH:mm';

export const EntityBlockoutsDialog: React.FC<EntityBlockoutsDialogProps> = ({
    entityId,
    entityName,
    blockouts: blockoutList,
    open,
    onClose,
}) => {
    const { t } = useTranslation('translation');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);

    const [createBlockout, { loading: creating }] = useMutation(BLOCKOUT_CREATE, {
        refetchQueries: [ENTITIES],
        awaitRefetchQueries: true,
    });
    const [deleteBlockout, { loading: deleting }] = useMutation(BLOCKOUT_DELETE, {
        refetchQueries: [ENTITIES],
        awaitRefetchQueries: true,
    });

    if (!open) return null;

    const blockouts = [...blockoutList].sort(
        (a, b) => dayjs(a.startDatetime).valueOf() - dayjs(b.startDatetime).valueOf(),
    );

    const handleCreate = async () => {
        setError(null);
        const from = dayjs(start, INPUT_FORMAT);
        const to = dayjs(end, INPUT_FORMAT);
        if (!start || !end || !from.isValid() || !to.isValid()) {
            setError(t('reservations.blockouts.errorRequired'));
            return;
        }
        if (!to.isAfter(from)) {
            setError(t('reservations.blockouts.errorOrder'));
            return;
        }
        try {
            const res = await createBlockout({
                variables: {
                    entityId,
                    startDatetime: from.format(),
                    endDatetime: to.format(),
                    reason: reason.trim() || null,
                },
            });
            if (res.errors) {
                setError(extractGraphQLError(res.errors, t('reservations.blockouts.errorGeneric')));
                return;
            }
            setStart('');
            setEnd('');
            setReason('');
        } catch (e) {
            setError(extractCaughtError(e, t('reservations.blockouts.errorGeneric')));
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm(t('reservations.blockouts.confirmDelete'))) return;
        setError(null);
        try {
            const res = await deleteBlockout({ variables: { id } });
            if (res.errors) {
                setError(extractGraphQLError(res.errors, t('reservations.blockouts.errorGeneric')));
            }
        } catch (e) {
            setError(extractCaughtError(e, t('reservations.blockouts.errorGeneric')));
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold mb-1">{t('reservations.blockouts.title')}</h3>
                <p className="text-sm text-gray-600 mb-4">{entityName}</p>

                {blockouts.length === 0 && (
                    <p className="text-sm text-gray-500 mb-4">{t('reservations.blockouts.empty')}</p>
                )}
                {blockouts.length > 0 && (
                    <ul className="divide-y mb-4">
                        {blockouts.map((b) => (
                            <li key={b.id} className="flex items-center justify-between gap-3 py-2">
                                <div>
                                    <div className="text-sm font-medium">
                                        {dayjs(b.startDatetime).format(DISPLAY_FORMAT)} –{' '}
                                        {dayjs(b.endDatetime).format(DISPLAY_FORMAT)}
                                    </div>
                                    {b.reason && <div className="text-xs text-gray-500">{b.reason}</div>}
                                </div>
                                <Button variant="destructive" onClick={() => handleDelete(b.id)} disabled={deleting}>
                                    {t('reservations.blockouts.delete')}
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('reservations.blockouts.from')}
                        </label>
                        <Input
                            type="datetime-local"
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            disabled={creating}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('reservations.blockouts.to')}
                        </label>
                        <Input
                            type="datetime-local"
                            value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            disabled={creating}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('reservations.blockouts.reason')}
                        </label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} disabled={creating} />
                    </div>
                </div>

                {error && (
                    <p role="alert" className="text-sm text-red-600 mb-4">
                        {error}
                    </p>
                )}

                <div className="flex gap-2">
                    <Button onClick={handleCreate} disabled={creating}>
                        {creating ? t('reservations.saving') : t('reservations.blockouts.add')}
                    </Button>
                    <Button variant="outline" onClick={onClose}>
                        {t('reservations.blockouts.close')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
